import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Banner, Button, type BannerLevel } from "./primitives.js";
import { useI18n } from "../i18n/react.js";

/**
 * **Tek slotlu, öncelikli durum bandı** — Rapor 14 §9, Rapor 15 F1-37.
 *
 * ### ⚠️ KURAL: ekranda aynı anda **BİR** durum mesajı bulunur
 * "Bağlantınız zayıf" + "Yeniden bağlanılıyor" + "Ses kilitli" + "Yeni sürüm
 * hazır" üst üste yığıldığında kullanıcı hiçbirini okumaz; ızgara aşağı
 * kayar ve mobilde video alanının yarısı yiter. Bu yüzden aday listesi
 * **tek bir kazanana** indirgenir.
 *
 * ### Seçim kuralı
 * 1. En yüksek **önem** kazanır (`error` > `warn` > `info`).
 * 2. Eşitlikte **liste sırası** belirler — çağıran taraf adayları önem
 *    sırasında yazar (önce eylem gerektirenler).
 *
 * ⚠️ Sıralama **kararlıdır**: aynı girdi hep aynı bandı üretir. Kararsız bir
 *    seçim, iki uyarı arasında titreyen bir bant demektir.
 */

export interface StatusCandidate {
  level: BannerLevel;
  message: string;
  action?: { label: string; onAct: () => void };
}

const RANK: Readonly<Record<BannerLevel, number>> = Object.freeze({
  error: 3,
  warn: 2,
  info: 1,
});

export function pickStatus(
  candidates: readonly (StatusCandidate | null | false | undefined)[],
): StatusCandidate | null {
  let best: StatusCandidate | null = null;
  for (const c of candidates) {
    if (!c) continue;
    // ⚠️ Kesin `>`: eşitlikte İLK aday kazanır (kararlı sıralama).
    if (best === null || RANK[c.level] > RANK[best.level]) best = c;
  }
  return best;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ **GEÇİCİ BİLDİRİM SÜRESİ — okuma hızından türetilir.**
 * ═══════════════════════════════════════════════════════════════════════════
 * Sabit bir süre (ör. 3 sn) iki ucu birden kaçırır: "Bağlandı" için uzun,
 * "Bağlantınız zayıf, görüntü kalitesi düşürüldü" için kısadır. Ortalama okuma
 * hızı ~200 kelime/dk ≈ 15 karakter/sn; buna okumaya başlama payı eklenir.
 *
 * ⚠️ Alt sınır 4 sn: daha kısası, gözü ekranın başka yerinde olan kullanıcı
 *    için hiç görünmemiş sayılır. Üst sınır 10 sn: geçici bildirim kalıcıya
 *    dönüşmemelidir — zaten kaldırmak istediğimiz davranış buydu.
 */
export function toastDuration(message: string): number {
  return Math.min(10_000, Math.max(4_000, 1_200 + message.length * 70));
}

/**
 * ⭐ **Kalıcı mı, geçici mi?**
 *
 * ⚠️ Ölçüt **kullanıcının yapacak bir şeyi olup olmadığıdır**, önem değil:
 *    - Eylem düğmesi taşıyan bir bildirim (Yeniden yükle, Sesi aç) kaybolursa
 *      kullanıcı o eyleme **bir daha ulaşamaz**.
 *    - `error` bir oturumun öldüğünü söyler; kaybolursa kullanıcı ekranın
 *      neden çalışmadığını açıklayan tek cümleyi kaybeder.
 *    İkisi de elle kapatılabilir; kilitli değildir.
 *
 * Geri kalan her şey (`info`, `warn`) geçicidir — kullanıcının şikâyet ettiği
 * "ekranda kalıcı duran uyarılar" tam olarak bunlardı.
 */
function isPersistent(c: StatusCandidate): boolean {
  return c.action !== undefined || c.level === "error";
}

export function StatusBand(props: {
  candidates: readonly (StatusCandidate | null | false | undefined)[];
  /**
   * `inline` — akış içinde bant (form hataları, yönetim konsolu).
   * `toast`  — ekranın sağ üstünde yüzen, süreli bildirim.
   *
   * ⚠️ Varsayılan `inline` OLMAK ZORUNDA: form hatası formun yanında durmalı.
   *    "Kod geçersiz" mesajını ekranın sağ üst köşesine göndermek, kullanıcının
   *    baktığı yerin dışında bir yere yazmaktır.
   */
  presentation?: "inline" | "toast";
}): JSX.Element | null {
  const winner = pickStatus(props.candidates);
  if (props.presentation !== "toast") {
    if (winner === null) return null;
    // ⚠️ `exactOptionalPropertyTypes`: `action={undefined}` yazmak yasaktır —
    //    özniteliğin YOK olması ile `undefined` olması aynı şey değildir.
    return winner.action === undefined ? (
      <Banner level={winner.level}>{winner.message}</Banner>
    ) : (
      <Banner level={winner.level} action={winner.action}>
        {winner.message}
      </Banner>
    );
  }
  return <StatusToast winner={winner} />;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 **UYARILAR EKRANDA KALICI OLARAK ASILI KALIYORDU.**
 * ═══════════════════════════════════════════════════════════════════════════
 * Bant, koşul doğru olduğu sürece görünürdü. "Bağlantınız zayıf" bir kez
 * yazıldıktan sonra ağ düzelene kadar orada durur; kullanıcı okumuş, anlamış
 * ve devam etmiştir ama uyarı hâlâ oradadır ve toplantı ekranından yer yer.
 *
 * ⭐ Artık geçici bildirimler **kendiliğinden kaybolur**. Koşul hâlâ doğru olsa
 *    bile bildirim geri GELMEZ: aynı şeyi ikinci kez söylemek bilgi değil
 *    gürültüdür. Mesaj değişirse (yeni bir durum) yeni bildirim çıkar.
 *
 * ### ⚠️ Sayaç fare/odak altında DURUR
 * WCAG 2.2.1 (Timing Adjustable): kullanıcı okurken içeriğin kaybolmaması
 * gerekir. Fareyi üzerine getirmek ya da klavyeyle odaklanmak sayacı
 * dondurur — okumaya başlamış birinin cümlesi elinden alınmaz.
 */
function StatusToast(props: { winner: StatusCandidate | null }): JSX.Element | null {
  const i18n = useI18n();
  const { winner } = props;

  /**
   * ⚠️ Kimlik **seviye + metinden** türetilir, nesne kimliğinden değil:
   *    `statusCandidates` her render'da yeniden kurulur, yani nesne kimliği
   *    her karede değişir. Ona bağlanmak sayacı her render'da sıfırlar ve
   *    bildirim hiç kaybolmaz — düzeltmek istediğimiz davranışın aynısı.
   */
  const key = winner === null ? null : `${winner.level}:${winner.message}`;
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);

  // Yeni bir mesaj geldiğinde önceki kapatma kararı geçersizdir.
  useEffect(() => {
    setDismissed(null);
  }, [key]);

  const persistent = winner !== null && isPersistent(winner);
  const shown = winner !== null && key !== null && dismissed !== key;

  /**
   * ═════════════════════════════════════════════════════════════════════════
   * 🚨 **SAYAÇ HER RENDER'DA SIFIRLANIYORDU — BİLDİRİM HİÇ KAYBOLMUYORDU.**
   * ═════════════════════════════════════════════════════════════════════════
   * Bağımlılık listesinde `winner` **nesnesi** vardı. `statusCandidates` her
   * render'da yeniden kurulan nesne değişmezlerinden oluşur; yani `winner`ın
   * kimliği her karede değişir. Toplantı ekranı ise sürekli render olur
   * (bağlantı durumu, katılımcı listesi, medya sayacı). Sonuç: `setTimeout`
   * her render'da temizlenip yeniden kuruluyor ve **hiçbir zaman ateşlenmiyor**
   * — yani bu bileşenin var oluş sebebi olan davranış çalışmıyordu.
   *
   * ⚠️ Bu tam olarak on satır yukarıda kendi yazdığım uyarının ihlaliydi.
   *    Uyarıyı `key` için yazıp bağımlılık listesinde `winner`ı bırakmak,
   *    kuralı bilip uygulamamaktır.
   *
   * ⭐ Artık listedeki her şey **ilkel**: `key` bir dizedir ve mesaj
   *    değişmedikçe aynı kalır. Süre için gereken metin de `key`in içindedir;
   *    ayrı bir nesneye bakmaya gerek yok.
   */
  useEffect(() => {
    if (!shown || persistent || paused || key === null) return;
    const message = key.slice(key.indexOf(":") + 1);
    const t = setTimeout(() => setDismissed(key), toastDuration(message));
    return () => clearTimeout(t);
  }, [shown, persistent, paused, key]);

  if (!shown || winner === null) return null;

  return (
    <div
      data-toast-layer
      /**
       * ⚠️ Kap `aria-live` TAŞIMAZ; duyuruyu `Banner` kendi `role`üyle yapar
       *    (`error` → `alert`, diğerleri → `status`). İkisi birden verilirse
       *    bazı ekran okuyucular mesajı iki kez okur.
       */
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div data-toast data-level={winner.level}>
        {winner.action === undefined ? (
          <Banner level={winner.level}>{winner.message}</Banner>
        ) : (
          <Banner level={winner.level} action={winner.action}>
            {winner.message}
          </Banner>
        )}
        {/*
          ⚠️ Kapatma düğmesi **her zaman** vardır, yalnız kalıcı olanlarda
             değil: geçici bildirim de kullanıcının işine engel olabilir ve
             onu beklemeye zorlamak gereksizdir.
        */}
        <Button
          size="sm"
          variant="ghost"
          label={i18n.t("common.close")}
          title={i18n.t("common.close")}
          onClick={() => setDismissed(key)}
        >
          <span aria-hidden="true">×</span>
        </Button>
      </div>
    </div>
  );
}
